import { ConversationRequest, Feedback } from "./models";
import type { CalculatorFormData, CalculatorResult } from "../components/Calculator/calculatorTypes";
import texts from "./texts-mock.json";
import tokenUsageMock from "./token-usage-mock.json";
import tokenLimitMock from "./token-limit-mock.json";
import conversationMock from "./conversation-mock.json";

let isDevMode = false;

export async function conversationApi(
  options: ConversationRequest,
  abortSignal: AbortSignal,
): Promise<Response> {
  if (isDevMode) {
    console.log("[DEV MODE] Using mock data for conversation API");
    //DEV-Mode: if you want to test the token limit reached error, uncomment the following line
    //throw new Error('TOKEN_LIMIT_REACHED');
    return new Response(JSON.stringify(conversationMock), {
      status: 200,
      headers: { "Content-Type": "application/json" }
    });
  }

  try {
    // Make the API call
    const response = await fetch("/api/rag/conversation", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        messages: options.messages,
        session_id: options.session_id,
        ...(options.calculator_report !== undefined && {
          calculator_report: options.calculator_report,
        }),
      }),
      signal: abortSignal
    });
    
    // Check if the response status is 402 (Payment Required) which indicates token limit reached
    if (response.status === 402) {
      console.log('Token limit reached (402 response)');
      throw new Error('TOKEN_LIMIT_REACHED');
    }
    
    return response;
  } catch (error) {
    // Re-throw the token limit error or any other error
    throw error;
  }
}

export async function loginCheckApi(): Promise<Response> {
  const response = await fetch("/api/login_check", {
    method: "GET",
  });

  return response;
}


export async function tokenUsageApi(): Promise<Response> {

  if (isDevMode) {
    console.log("[DEV MODE] Using mock data for token usage API");
    return new Response(JSON.stringify(tokenUsageMock), {
      status: 200,
      headers: { "Content-Type": "application/json" },
    });
  }

  return await fetch("/api/cost/token_usage_per_user", {
    method: "GET",
  });
}

/**
 * Checks if the token limit has been reached by calling tokenLimitApi and tokenUsageApi
 * @returns Promise<boolean> - true if token limit is reached, false otherwise
 */
export async function checkTokenLimit(): Promise<boolean> {
  try {
    const limitResponse = await tokenLimitApi();
    const usageResponse = await tokenUsageApi();
    
    if (!limitResponse.ok || !usageResponse.ok) {
      console.error('Failed to fetch token information');
      return false;
    }
    
    const limitData = await limitResponse.json();
    const usageData = await usageResponse.json();
    
    // Check if usage has reached or exceeded the limit
    return usageData.usage >= limitData.limit;
  } catch (error) {
    console.error('Error checking token limit:', error);
    return false;
  }
}

export async function tokenLimitApi(): Promise<Response> {
  if (isDevMode) {
    console.log("[DEV MODE] Using mock data for token limit API");
    return new Response(JSON.stringify(tokenLimitMock), {
      status: 200,
      headers: { "Content-Type": "application/json" },
    });
  }

  return await fetch("/api/cost/token_limit_per_user", {
    method: "GET",
  });
}

// Submit user feedback
export async function submitFeedback(feedback: Feedback): Promise<void> {
  if (isDevMode) {
    console.log("[DEV MODE] Mock feedback submission:", feedback);
    return;
  }

  const response = await fetch("/api/feedback/submit", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      message_id: feedback.messageId,
      ...(feedback.traceId && { trace_id: feedback.traceId }),
      ...(feedback.reasons && { reasons: feedback.reasons }),
      ...(feedback.comment && { comment: feedback.comment }),
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to submit feedback: ${response.statusText}`);
  }
}

const CALCULATOR_API_URL = 'https://api.gentlesky-c73d11bc.westeurope.azurecontainerapps.io/calculate';

function toNum(value: string): number {
  const n = parseInt(value, 10);
  return isNaN(n) ? 0 : Math.max(0, n);
}

export async function calculateApi(formData: CalculatorFormData): Promise<CalculatorResult> {
  const childrenTotal =
    formData.children_0_5 +
    formData.children_6_13 +
    formData.children_14_17 +
    formData.children_18_24_non_working;

  const requestBody = {
    partnerschaft: formData.shared_household,
    kinder_0: formData.children_0_5,
    kinder_6: formData.children_6_13,
    kinder_14: formData.children_14_17,
    kinder_18: formData.children_18_24_non_working,
    kaltmiete: formData.checkbox_housing_costs ? 0 : toNum(formData.cold_rent),
    nebenkosten: formData.checkbox_housing_costs ? 0 : toNum(formData.additional_costs),
    heizkosten: formData.checkbox_housing_costs ? 0 : toNum(formData.heating_costs),
    schwanger: formData.pregnant,
    brutto_einkommen: formData.checkbox_income_user ? 0 : toNum(formData.income_user_gross),
    netto_einkommen: formData.checkbox_income_user ? 0 : toNum(formData.income_user_net),
    brutto_einkommen_partner: !formData.shared_household || formData.checkbox_income_partner ? 0 : toNum(formData.income_partner_gross),
    netto_einkommen_partner: !formData.shared_household || formData.checkbox_income_partner ? 0 : toNum(formData.income_partner_net),
    einkommen_vermietung: formData.checkbox_other_income ? 0 : toNum(formData.rental_income),
    alg1: formData.checkbox_other_income ? 0 : toNum(formData.unemployment_benefit),
    renten: formData.checkbox_other_income ? 0 : toNum(formData.pension),
    sonstiges: formData.checkbox_other_income ? 0 : toNum(formData.other_income_amount),
    alter_kind1: (childrenTotal === 1 || childrenTotal === 2) ? formData.child_1_age : null,
    alter_kind2: childrenTotal === 2 ? formData.child_2_age : null,
  };

  const response = await fetch(CALCULATOR_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    throw new Error(`Berechnungs-API Fehler: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();

  return {
    grundsicherung: Math.round(data.anspruch),
    bedarf: Math.round(data.bedarf),
    totalAnrechenbares: Math.round(data.anzurechnendes_einkommen),
    formData,
  };
}

export async function fetchTextsApi(): Promise<Response> {
  if (isDevMode) {
    console.log("[DEV MODE] Using mock data for texts API");
    return new Response(JSON.stringify(texts), {
      status: 200,
      headers: { "Content-Type": "application/json" },
    });
  }

  return await fetch("/api/texts/download/all.json", {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
}
